import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Download, Loader2, FileSignature } from "lucide-react";
import { Button } from "../components/ui/button";
import api, { apiError } from "../lib/api";

function FieldInput({ f, value, onChange }) {
  const w = Math.min(360, Math.max(90, (value?.length || (f.label?.length || 8)) * 8 + 24));
  return (
    <input
      value={value || ""}
      onChange={(e) => onChange(f.id, e.target.value)}
      placeholder={f.label || "fill"}
      title={f.context || f.label}
      data-testid={`cf-field-${f.id}`}
      className={`inline-block align-baseline mx-0.5 px-1 py-0 text-[13px] border-b bg-transparent outline-none focus:border-emerald-500 ${value ? "border-emerald-500/60 text-emerald-300" : "border-amber-500/70 text-amber-200 placeholder:text-amber-500/50"}`}
      style={{ width: w }}
    />
  );
}

function Segments({ segs, byId, values, onChange }) {
  return (
    <>
      {segs.map((s, i) =>
        "t" in s ? (
          <span key={i}>{s.t}</span>
        ) : (
          <FieldInput key={i} f={byId[s.field] || { id: s.field }} value={values[s.field]} onChange={onChange} />
        )
      )}
    </>
  );
}

export default function ContractFill() {
  const { id, resource } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(sp.get("template") || "");
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    api.get("/contracts/library-templates").then((r) => {
      const its = (r.data.items || []).filter((t) => t.is_docx);
      setTemplates(its);
      if (!templateId && its[0]) setTemplateId(its[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const loadForm = useCallback(() => {
    if (!templateId) return;
    setLoading(true);
    api.get(`/contracts/${templateId}/form?record_id=${id}`).then((r) => {
      setData(r.data);
      const init = {};
      (r.data.fields || []).forEach((f) => { init[f.id] = f.value || ""; });
      setValues(init);
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [templateId, id]);
  useEffect(() => { loadForm(); }, [loadForm]);

  const byId = useMemo(() => {
    const m = {};
    (data?.fields || []).forEach((f) => { m[f.id] = f; });
    return m;
  }, [data]);
  const setVal = (fid, v) => setValues((s) => ({ ...s, [fid]: v }));
  const emptyCount = (data?.fields || []).filter((f) => !(values[f.id] || "").trim()).length;
  const totalCount = (data?.fields || []).length;

  const askAngela = async () => {
    setAiBusy(true);
    const tid = toast.loading("Angela is reading the contract and filling blanks…");
    try {
      const fields = (data.fields || []).map((f) => ({ id: f.id, label: f.label, context: f.context, value: values[f.id] || "" }));
      const { data: res } = await api.post("/contracts/ai-fill", { record_id: id, template_id: templateId, fields });
      const vals = res.values || {};
      const n = Object.keys(vals).length;
      if (n === 0) { toast.info("Angela couldn't confidently fill any remaining blanks"); }
      else { setValues((s) => ({ ...s, ...vals })); toast.success(`Angela filled ${n} field${n > 1 ? "s" : ""}`); }
    } catch (e) { toast.error(apiError(e)); } finally { toast.dismiss(tid); setAiBusy(false); }
  };

  const [coverLetter, setCoverLetter] = useState(false);

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data: res } = await api.post("/contracts/generate", { record_id: id, template_id: templateId, name: data.name, field_values: values, include_cover_letter: coverLetter });
      const dl = await api.get(`/documents/${res.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement("a"); a.href = url; a.download = `${data.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Contract saved to Documents & downloaded");
    } catch (e) { toast.error(apiError(e)); } finally { setGenBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contract-fill-page">
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur px-4 sm:px-8 py-3">
        <div className="max-w-[900px] mx-auto flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/record/${resource}/${id}`)} data-testid="cf-back">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Back
          </Button>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <FileSignature className="h-4 w-4 text-primary" />{data?.name || "Contract"}
          </div>
          {templates.length > 1 && (
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="cf-template"
              className="h-8 rounded-md border border-input bg-background px-2 text-sm">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground" data-testid="cf-empty-count">{emptyCount} of {totalCount} blank</span>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="cf-cover-letter-label">
              <input type="checkbox" checked={coverLetter} onChange={(e) => setCoverLetter(e.target.checked)} className="h-3.5 w-3.5 accent-primary" data-testid="cf-cover-letter-toggle" />
              Cover letter
            </label>
            <Button variant="outline" size="sm" onClick={askAngela} disabled={aiBusy || loading || !totalCount} data-testid="cf-ask-angela">
              {aiBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}Ask Angela to fill
            </Button>
            <Button size="sm" onClick={generate} disabled={genBusy || loading || !data} data-testid="cf-generate">
              {genBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}Generate & Save PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !data ? (
          <div className="text-center py-24 text-muted-foreground">No fillable template available. Upload a .docx contract in Operations → Contracts.</div>
        ) : (
          <div className="max-w-[900px] mx-auto bg-card border border-border rounded-lg shadow-sm px-8 sm:px-14 py-10 leading-7 text-[13px] text-foreground/90" data-testid="contract-body">
            {(data.blocks || []).map((b, i) => {
              if (b.type === "space") return <div key={i} className="h-3" />;
              if (b.type === "heading")
                return <h3 key={i} className="text-base font-bold text-foreground mt-6 mb-2"><Segments segs={b.segments} byId={byId} values={values} onChange={setVal} /></h3>;
              if (b.type === "para")
                return <p key={i} className="mb-2"><Segments segs={b.segments} byId={byId} values={values} onChange={setVal} /></p>;
              if (b.type === "table")
                return (
                  <table key={i} className="w-full my-3 border-collapse text-[12.5px]">
                    <tbody>
                      {b.rows.map((cells, ri) => (
                        <tr key={ri}>
                          {cells.map((segs, ci) => (
                            <td key={ci} className="border border-border align-top px-2 py-1.5">
                              <Segments segs={segs} byId={byId} values={values} onChange={setVal} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              return null;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
